import { Controller } from "@hotwired/stimulus"

// Drag-and-drop photo upload controller for DPK photos.
// Handles file selection, client-side validation, preview grid, and AJAX upload.
export default class extends Controller {
  static targets = ["dropZone", "fileInput", "previewGrid", "uploadButton", "applyAllCheckbox",
                     "globalPhotographer", "progressArea", "fileCount", "preparing"]
  static values = { url: String, statusUrl: String, maxSize: { type: Number, default: 10485760 } }

  connect() {
    this.pendingFiles = []
    this.busy = false
    this.csrfToken = document.querySelector('meta[name="csrf-token"]')?.content || ""
    // A page Turbo brings back from its cache (the Back button) comes back as
    // it was left, mid-batch; start clean instead (workflow seat, 2026-10-21).
    this.previewGridTarget.innerHTML = ""
    this.previewGridTarget.classList.add("hidden")
    this.uploadButtonTarget.classList.add("hidden")
    this.hideStatus()
    this.resetUploadButton()
  }

  disconnect() {
    clearTimeout(this.preparingTimer)
  }

  // --- Drag and drop handlers ---

  dragOver(event) {
    event.preventDefault()
    event.stopPropagation()
    this.dropZoneTarget.classList.add("border-[var(--color-mc-accent-terracotta)]", "bg-[var(--color-mc-accent-terracotta)]/5")
  }

  dragLeave(event) {
    event.preventDefault()
    event.stopPropagation()
    this.dropZoneTarget.classList.remove("border-[var(--color-mc-accent-terracotta)]", "bg-[var(--color-mc-accent-terracotta)]/5")
  }

  drop(event) {
    event.preventDefault()
    event.stopPropagation()
    this.dropZoneTarget.classList.remove("border-[var(--color-mc-accent-terracotta)]", "bg-[var(--color-mc-accent-terracotta)]/5")

    const files = Array.from(event.dataTransfer.files)
    this.addFiles(files)
  }

  // --- File input handler ---

  selectFiles(event) {
    if (event) event.stopPropagation()
    this.fileInputTarget.click()
  }

  filesSelected(event) {
    const files = Array.from(event.target.files)
    this.addFiles(files)
    event.target.value = ""
  }

  // --- File validation and preview ---

  addFiles(files) {
    // One batch at a time: files added while the last one is still going up
    // or being prepared would be thrown away by its reload.
    if (this.busy) {
      if (window.showToast) window.showToast("info", "Wait until these photos are ready, then add more.")
      return
    }
    const allowedTypes = ["image/jpeg", "image/png", "image/webp", "image/gif"]
    let addedCount = 0

    files.forEach(file => {
      if (!allowedTypes.includes(file.type)) {
        if (window.showToast) window.showToast("error", `${file.name}: Only JPG, PNG, WEBP, and GIF files are accepted.`)
        return
      }
      if (file.size > this.maxSizeValue) {
        if (window.showToast) window.showToast("error", `${file.name}: Exceeds the 10MB limit.`)
        return
      }

      const id = crypto.randomUUID()
      this.pendingFiles.push({ id, file, photographer: "" })
      this.renderPreviewItem(id, file)
      addedCount++
    })

    if (addedCount > 0) {
      this.updateUploadButton()
      this.previewGridTarget.classList.remove("hidden")
      this.uploadButtonTarget.classList.remove("hidden")
    }
  }

  renderPreviewItem(id, file) {
    const item = document.createElement("div")
    item.className = "flex items-center gap-3 p-3 bg-[var(--color-mc-bg)] rounded-lg border border-[var(--color-mc-border)]"
    item.dataset.photoUploadFileId = id

    const sizeMB = (file.size / 1024 / 1024).toFixed(1)

    item.innerHTML = `
      <div class="w-16 h-12 rounded bg-[var(--color-mc-border)] flex-shrink-0 overflow-hidden">
        <img class="preview-thumb w-full h-full object-cover" alt="">
      </div>
      <div class="flex-1 min-w-0">
        <p class="text-sm text-[var(--color-mc-text)] truncate">${this.esc(file.name)}</p>
        <p class="text-xs text-[var(--color-mc-text-secondary)]">${sizeMB} MB</p>
      </div>
      <input type="text" placeholder="Photographer" data-photo-upload-file-id="${id}"
             data-action="input->photo-upload#photographerChanged"
             class="mc-input text-sm w-40">
      <button type="button" data-action="click->photo-upload#removeFile"
              data-photo-upload-file-id="${id}"
              class="p-1 text-[var(--color-mc-text-secondary)] hover:text-red-500 transition-colors cursor-pointer">
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
        </svg>
      </button>
    `

    // Set thumbnail using object URL (no broken image icon)
    const img = item.querySelector(".preview-thumb")
    if (img) {
      const objectUrl = URL.createObjectURL(file)
      img.src = objectUrl
      img.onload = () => URL.revokeObjectURL(objectUrl)
    }

    this.previewGridTarget.appendChild(item)
  }

  removeFile(event) {
    const id = event.currentTarget.dataset.photoUploadFileId
    this.pendingFiles = this.pendingFiles.filter(f => f.id !== id)
    const item = this.previewGridTarget.querySelector(`[data-photo-upload-file-id="${id}"]`)
    if (item) item.remove()
    this.updateUploadButton()

    if (this.pendingFiles.length === 0) {
      this.previewGridTarget.classList.add("hidden")
      this.uploadButtonTarget.classList.add("hidden")
    }
  }

  photographerChanged(event) {
    const id = event.target.dataset.photoUploadFileId
    const entry = this.pendingFiles.find(f => f.id === id)
    if (entry) entry.photographer = event.target.value
  }

  applyToAll() {
    const globalValue = this.globalPhotographerTarget.value
    if (!globalValue) return

    this.pendingFiles.forEach(f => { f.photographer = globalValue })
    this.previewGridTarget.querySelectorAll("input[data-photo-upload-file-id]").forEach(input => {
      input.value = globalValue
    })
  }

  updateUploadButton() {
    const count = this.pendingFiles.length
    if (this.hasFileCountTarget) {
      this.fileCountTarget.textContent = count
    }
    this.uploadButtonTarget.disabled = count === 0
  }

  // --- Upload ---

  async upload(event) {
    event.preventDefault()
    if (this.busy || this.pendingFiles.length === 0) return

    this.busy = true
    this.hideStatus()
    this.uploadButtonTarget.disabled = true
    this.uploadButtonTarget.textContent = "Uploading..."

    const isPublic = this.element.querySelector("[name='is_public']")?.checked ? "1" : "0"
    let successCount = 0
    // Only what failed stays on the list, so Upload never sends a photo twice
    // (workflow seat MUST, 2026-10-21).
    const failed = []
    // Photos the server is still preparing on its worker (2026-10-21): shown
    // as Preparing here and watched until they are ready.
    const preparing = []

    for (const entry of this.pendingFiles) {
      const item = this.previewGridTarget.querySelector(`[data-photo-upload-file-id="${entry.id}"]`)

      const formData = new FormData()
      formData.append("images[]", entry.file)
      formData.append("photographer", entry.photographer || this.globalPhotographerTarget.value || "")
      formData.append("is_public", isPublic)

      try {
        const response = await fetch(this.urlValue, {
          method: "POST",
          headers: {
            "X-CSRF-Token": this.csrfToken,
            "Accept": "application/json"
          },
          body: formData
        })

        // Check HTTP status before trying to parse JSON
        if (!response.ok) {
          failed.push(entry)
          if (window.showToast) window.showToast("error", `${entry.file.name}: Server error (${response.status})`)
          this.markRow(item, "failed")
          continue
        }

        const data = await response.json()
        const result = data.photos?.[0]

        if (result?.success && result.pending) {
          preparing.push({ id: result.upload_id, name: entry.file.name, item, entry })
          this.markRow(item, "preparing")
        } else if (result?.success) {
          successCount++
          this.markRow(item, "done")
          if (result.resized && window.showToast) {
            window.showToast("info", `${entry.file.name} was resized to fit within 3000px.`)
          }
        } else {
          failed.push(entry)
          const errorMsg = result?.error || "Upload failed"
          if (window.showToast) window.showToast("error", `${entry.file.name}: ${errorMsg}`)
          this.markRow(item, "failed")
        }
      } catch (err) {
        failed.push(entry)
        console.error("Photo upload error:", err)
        if (window.showToast) window.showToast("error", `${entry.file.name}: ${err.message}`)
        this.markRow(item, "failed")
      }
    }
    this.pendingFiles = failed

    // She went to another press kit tab while the batch ran: say what landed
    // and never reload the page she is on now.
    if (!this.element.isConnected) {
      const landed = successCount + preparing.length
      const still = preparing.length > 0 ? ` (${preparing.length} still being prepared)` : ""
      if (landed > 0 && window.showToast) window.showToast("success", `${landed} photo${landed !== 1 ? "s" : ""} uploaded to the press kit${still}.`)
      return
    }

    if (successCount > 0 && window.showToast) {
      window.showToast("success", `${successCount} photo${successCount !== 1 ? "s" : ""} uploaded successfully.`)
    }
    if (preparing.length > 0) {
      this.watchPreparing(preparing, successCount > 0)
    } else {
      this.batchOver(successCount > 0)
    }
  }

  // The batch is settled: reload only when nothing failed, so a failure is
  // never wiped before she has read it (workflow seat, 2026-10-21); otherwise
  // name what failed and leave exactly those on the list for Upload to retry.
  batchOver(anyReady) {
    this.busy = false
    if (this.pendingFiles.length === 0) {
      if (anyReady) setTimeout(() => this.reloadIfStillHere(), 1000)
      this.resetUploadButton()
      return
    }
    const names = this.pendingFiles.map(e => e.file.name).join(", ")
    const one = this.pendingFiles.length === 1
    this.showStatus(`${names} could not be added. Press Upload to try ${one ? "it" : "them"} again${anyReady ? ", or reload the page to see the photos that are ready" : ""}.`)
    this.resetUploadButton()
  }

  // --- Photos still being prepared ---

  markRow(item, state) {
    const thumb = item?.querySelector(".preview-thumb")
    if (!item || !thumb) return
    thumb.classList.remove("ring-amber-400", "ring-green-500", "ring-red-500")
    thumb.classList.add("ring-2", { preparing: "ring-amber-400", done: "ring-green-500", failed: "ring-red-500" }[state])
    item.classList.toggle("opacity-50", state === "done")
    // A photo already sent cannot be taken back from here; without its X, one
    // click cannot empty the list and hide the Upload button a late failure
    // will need (workflow seat, 2026-10-21).
    if (state !== "failed") item.querySelector("button[data-action~='click->photo-upload#removeFile']")?.remove()
    let label = item.querySelector(".prepare-label")
    if (state === "preparing") {
      if (!label) {
        label = document.createElement("span")
        label.className = "prepare-label text-xs font-medium text-amber-600 whitespace-nowrap"
        item.querySelector(".preview-thumb")?.closest("div")?.after(label)
      }
      label.textContent = "Preparing"
    } else if (label) {
      label.remove()
    }
  }

  // Asks the server every 5 seconds how the waiting photos are doing, and
  // settles the batch once none is waiting, but only while she is still on
  // this page.
  watchPreparing(preparing, anyReady) {
    const started = Date.now()
    this.showPreparing(preparing)

    const tick = async () => {
      if (!this.element.isConnected) return
      const params = new URLSearchParams()
      preparing.forEach(p => params.append("ids[]", p.id))
      try {
        const response = await fetch(`${this.statusUrlValue}?${params}`, { headers: { "Accept": "application/json" } })
        if (response.ok) {
          const data = await response.json()
          for (const upload of data.uploads || []) {
            const index = preparing.findIndex(p => p.id === upload.id)
            if (index < 0 || upload.state === "waiting") continue
            const [photo] = preparing.splice(index, 1)
            if (upload.state === "done") {
              anyReady = true
              this.markRow(photo.item, "done")
            } else {
              // Back on the list, so Upload retries exactly this one.
              this.pendingFiles.push(photo.entry)
              this.markRow(photo.item, "failed")
              if (window.showToast) window.showToast("error", `${photo.name}: ${upload.error || "This photo could not be prepared. Please upload it again."}`)
            }
          }
        }
      } catch (err) {
        // A missed check is tried again on the next tick.
      }

      if (preparing.length === 0) {
        this.batchOver(anyReady)
        return
      }
      if (Date.now() - started > 180000) {
        const names = preparing.map(p => p.name).join(", ")
        const one = preparing.length === 1
        this.showStatus(`${names} ${one ? "is" : "are"} taking longer than usual. Reload this page in a few minutes to see ${one ? "it" : "them"}. If ${one ? "it is" : "they are"} not there in 15 minutes, upload ${one ? "it" : "them"} again.`)
        this.busy = false
        this.resetUploadButton()
        return
      }
      this.showPreparing(preparing)
      this.preparingTimer = setTimeout(tick, 5000)
    }
    this.preparingTimer = setTimeout(tick, 5000)
  }

  showPreparing(preparing) {
    const n = preparing.length
    this.showStatus(`${n} photo${n === 1 ? " is" : "s are"} still being prepared and will appear here when ready. Please don't upload ${n === 1 ? "it" : "them"} again.`)
  }

  showStatus(text) {
    if (!this.hasPreparingTarget) return
    this.preparingTarget.textContent = text
    this.preparingTarget.classList.remove("hidden")
  }

  hideStatus() {
    if (!this.hasPreparingTarget) return
    this.preparingTarget.textContent = ""
    this.preparingTarget.classList.add("hidden")
  }

  // The button as the page draws it, counting what is on the list now (the
  // "Uploading..." label replaced its count).
  resetUploadButton() {
    this.uploadButtonTarget.innerHTML = `<svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"/></svg>Upload <span data-photo-upload-target="fileCount">${this.pendingFiles.length}</span> Photo(s)`
    this.updateUploadButton()
  }

  reloadIfStillHere() {
    if (this.element.isConnected) window.location.reload()
  }

  esc(text) {
    const div = document.createElement("div")
    div.textContent = text || ""
    return div.innerHTML
  }
}
